/**
Author - Venkatesh Pullaganti
Git - https://github.com/venkatesh1146
**/

import React, {useState} from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import parse from 'html-react-parser';

import {ActionButton, Dropdown} from 'screens/commons';
import IMAGES from 'constants/Images';

import {
  ExtraSpace,
  Wrapper,
  SubHeader,
  UploadImage,
  UploadIcon,
  UploadMessage,
  DropZoneContainer,
  EachOption,
  PasswordInput,
  DropContainer,
  PasswordInputContainer,
  FilePreview,
  FileHeader,
  FileIcon,
  FileName,
  FileInfo,
  FileSize,
  FileActions,
  FileButton,
  UploadIconContainer,
  TextContainer,
  UploadDescription,
  UploadButton,
  BodyContainer,
  SupportedFormats,
  PasswordBox,
  PasswordLabel,
  LockIcon,
  PasswordText,
  MainUploadSection,
  CardFooter,
  CommonInstructionsContainer,
} from './UploadTwoSidedDoc.styles';

const propTypes = {
  handleImageUpload: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array.isRequired,
  handleDeleteUploadedFile: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  heading: PropTypes.string.isRequired,
  subHeading: PropTypes.string.isRequired,
  documentOptions: PropTypes.array,
  allowPasswordProtected: PropTypes.bool,
  setSelectedOption: PropTypes.func.isRequired,
  selectedOption: PropTypes.object,
  password: PropTypes.object,
  setPassword: PropTypes.func,
};

const UploadTwoSidedDocument = ({
  isLoading,
  isDisabled,
  handleImageUpload,
  uploadedFiles,
  handleDeleteUploadedFile,
  onSubmit,
  heading,
  subHeading,
  documentOptions = [],
  allowPasswordProtected,
  setSelectedOption,
  selectedOption,
  password,
  setPassword,
}) => {
  const fileIcon = IMAGES?.fileIcon || 'https://i.wlycdn.com/wealthy-home-page/file-icon.svg';

  const [isDragging, setIsDragging] = useState(false);

  const dropdownOptions = documentOptions.length
    ? documentOptions?.map((eachDeclaration) => (
        <EachOption key={eachDeclaration.key} onClick={() => setSelectedOption(eachDeclaration)}>
          {parse(eachDeclaration?.render)}
        </EachOption>
      ))
    : [];

  const CommonInstructions = () => (
    <CommonInstructionsContainer>
      <p className="body-small my-0">
        ● Supported file formats include only png, jpg, jpeg and pdf
      </p>
      <p className="body-small my-1">
        ● If both sides of the document are in single file please upload it on both front and back
        sides
      </p>
    </CommonInstructionsContainer>
  );

  // Function to get the front or back side file
  const getSideFile = (side) => uploadedFiles.find((file) => file.id === side);

  // Front and back side files
  const frontSideFile = getSideFile('front_side');
  const backSideFile = getSideFile('back_side');

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  return (
    <Wrapper className="custom-scrollbar">
      {subHeading ? <SubHeader dangerouslySetInnerHTML={{__html: subHeading}} /> : null}
      <BodyContainer>
        {documentOptions.length > 0 && (
          <>
            <Dropdown
              options={dropdownOptions}
              title={selectedOption?.render ? parse(selectedOption?.render) : 'Choose an option'}
              size="small"
            />
          </>
        )}

        <DropContainer>
          <DropZoneContainer>
            <UploadMessage className="body-medium">Front side</UploadMessage>
            <Dropzone
              onDrop={(file) => handleImageUpload(file, 'front_side')}
              multiple={false}
              accept={{
                'image/png': ['.png'],
                'image/jpeg': ['.jpg', '.jpeg'],
                'application/pdf': ['.pdf'],
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {({getRootProps, getInputProps}) => (
                <DropZoneContainer
                  {...getRootProps()}
                  className="dropzone-area"
                  isDragging={isDragging}
                  hasFiles={!!frontSideFile}
                >
                  <input {...getInputProps()} id="front-side-upload" />

                  {!frontSideFile ? (
                    <>
                      <MainUploadSection>
                        <UploadIconContainer>
                          <UploadIcon src={IMAGES.fileUploadImage} alt="Upload" />
                        </UploadIconContainer>
                        <TextContainer>
                          <UploadDescription>Drag 'n' drop the document here or</UploadDescription>
                          <UploadButton className="mt-3">Click to select files</UploadButton>
                        </TextContainer>
                      </MainUploadSection>
                    </>
                  ) : (
                    <FilePreview>
                      <FileHeader>
                        <FileIcon src={fileIcon} alt="File" />
                        <FileName className="body-medium">{frontSideFile.name}</FileName>
                      </FileHeader>
                      <FileInfo>
                        <FileSize className="body-small">
                          {(frontSideFile.size * 0.001).toFixed(2)} KB
                        </FileSize>
                      </FileInfo>
                      <FileActions>
                        <FileButton
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            document.getElementById('front-side-upload').click();
                          }}
                        >
                          Change File
                        </FileButton>
                        <FileButton
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteUploadedFile(frontSideFile);
                          }}
                        >
                          Remove
                        </FileButton>
                      </FileActions>
                    </FilePreview>
                  )}
                </DropZoneContainer>
              )}
            </Dropzone>
          </DropZoneContainer>

          <div className="divider" />

          <DropZoneContainer>
            <UploadMessage className="body-medium">Back side</UploadMessage>
            <Dropzone
              onDrop={(file) => handleImageUpload(file, 'back_side')}
              multiple={false}
              accept={{
                'image/png': ['.png'],
                'image/jpeg': ['.jpg', '.jpeg'],
                'application/pdf': ['.pdf'],
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {({getRootProps, getInputProps}) => (
                <DropZoneContainer
                  {...getRootProps()}
                  className="dropzone-area"
                  isDragging={isDragging}
                  hasFiles={!!backSideFile}
                >
                  <input {...getInputProps()} id="back-side-upload" />

                  {!backSideFile ? (
                    <>
                      <MainUploadSection>
                        <UploadIconContainer>
                          <UploadIcon src={IMAGES.fileUploadImage} alt="Upload" />
                        </UploadIconContainer>
                        <TextContainer>
                          <UploadDescription>Drag 'n' drop the document here or</UploadDescription>
                          <UploadButton className="mt-3">Click to select files</UploadButton>
                        </TextContainer>
                      </MainUploadSection>
                    </>
                  ) : (
                    <FilePreview>
                      <FileHeader>
                        <FileIcon src={fileIcon} alt="File" />
                        <FileName className="body-medium">{backSideFile.name}</FileName>
                      </FileHeader>
                      <FileInfo>
                        <FileSize className="body-small">
                          {(backSideFile.size * 0.001).toFixed(2)} KB
                        </FileSize>
                      </FileInfo>
                      <FileActions>
                        <FileButton
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            document.getElementById('back-side-upload').click();
                          }}
                        >
                          Change File
                        </FileButton>
                        <FileButton
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteUploadedFile(backSideFile);
                          }}
                        >
                          Remove
                        </FileButton>
                      </FileActions>
                    </FilePreview>
                  )}
                </DropZoneContainer>
              )}
            </Dropzone>
          </DropZoneContainer>
        </DropContainer>

        <PasswordText>
          <CommonInstructions />
        </PasswordText>

        <PasswordBox>
          {allowPasswordProtected ? (
            <>
              <PasswordLabel className="mb-2">
                <LockIcon src={IMAGES.lockIcon} alt="Lock" />
                <PasswordText>
                  Attention: Please provide passwords in the below inputs if they are
                  password-protected documents.
                </PasswordText>
              </PasswordLabel>

              <PasswordInputContainer>
                <p className="body-small text-black font-regular">Front side document password:</p>
                <PasswordInput
                  placeholder="Type your front side doc password here..."
                  type="password"
                  value={password.frontSide}
                  onChange={(event) =>
                    setPassword({
                      ...password,
                      frontSide: event.target.value,
                    })
                  }
                />
              </PasswordInputContainer>

              <PasswordInputContainer>
                <p className="body-small text-black font-regular">Back side document password:</p>
                <PasswordInput
                  placeholder="Type your back side doc password here..."
                  type="password"
                  value={password.backSide}
                  onChange={(event) =>
                    setPassword({
                      ...password,
                      backSide: event.target.value,
                    })
                  }
                />
              </PasswordInputContainer>
            </>
          ) : (
            <>
              <PasswordLabel>
                <LockIcon src={IMAGES.lockIcon} alt="Lock" />
                <PasswordText>
                  Attention: Please do not upload password-protected documents.
                </PasswordText>
              </PasswordLabel>
            </>
          )}
        </PasswordBox>
      </BodyContainer>

      <CardFooter>
        <ActionButton
          buttonText="Upload & Continue"
          callback={onSubmit}
          isLoading={isLoading}
          isDisabled={isDisabled}
          className="my-2 upload-doc-button"
        />
      </CardFooter>
    </Wrapper>
  );
};

UploadTwoSidedDocument.propTypes = propTypes;

export default UploadTwoSidedDocument;
